import { Offer } from 'api/ReportsApi';
import { OfferCardBadgeType } from 'components/OfferCard';
import { LoanGoal } from 'handlers/yourGoal';
import { formatNumber } from 'utils/formatNumber';
import { LARGE_LOAN_STEP_SIZE } from 'components/AdditionalFunds/additionalFundsUtils';

export interface HeaderInfo {
  title: string;
  showSubtitle: boolean;
}

export interface OfferByAmount {
  paymentPerPaycheck: number;
  apr: number;
  totalInterestRate: number;
  baseInterestRate: number;
  totalPayments: number;
  totalMonthsSaved?: number;
  totalInterestSavings?: number;
  achPaymentAmount?: number;
}

export const getSliderStep = (offers: Offer[]): number => {
  const firstOffer = offers[0];
  const firstLoanAmount = firstOffer?.calculations[0]?.loanAmount;
  const followingLoanAmount = firstOffer?.calculations[1]?.loanAmount;

  if (typeof firstLoanAmount !== 'number' || typeof followingLoanAmount !== 'number') return LARGE_LOAN_STEP_SIZE;

  return Math.abs(followingLoanAmount - firstLoanAmount);
};

export const getSliderMax = (offers: Offer[]): number => {
  let max = 0;
  offers.forEach((offer) => {
    const lastLoanAmount = offer?.calculations[offer.calculations.length - 1]?.loanAmount;
    if (typeof lastLoanAmount === 'number' && lastLoanAmount > max) max = lastLoanAmount;
  });
  return max;
};

export const isValidOfferAmount = (amount: number, offers: Offer[]): boolean =>
  offers.some((offer) => offer.calculations.some((calculation) => calculation.loanAmount === amount));

export const getOffersForAmount = (offersByTerm: Offer[], amount: number): OfferByAmount[] => {
  const offersByAmount: OfferByAmount[] = [];

  offersByTerm.forEach((term) => {
    const termAmount = term.calculations.find((t) => t.loanAmount === amount);
    if (!termAmount) return;

    offersByAmount.push({
      paymentPerPaycheck: termAmount.paymentAmount,
      totalInterestRate: termAmount.interestRate,
      baseInterestRate: termAmount.baseInterestRate,
      apr: termAmount.apr,
      totalPayments: term.term,
      totalMonthsSaved: termAmount.totalMonthsSaved,
      totalInterestSavings: termAmount.totalInterestSavings,
      achPaymentAmount: termAmount.achPaymentAmount,
    });
  });

  return offersByAmount;
};

export const sortOffersByGoals = (offers: OfferByAmount[], goals: LoanGoal[]): OfferByAmount[] => {
  // Create a copy to avoid mutating the original array
  const sortedOffers = [...offers];

  // Default: sort by term low to high (faster payment)
  if (goals.includes(LoanGoal.GetOutDebtFaster) || goals.includes(LoanGoal.LowerInterestRate) || goals.length === 0) {
    return sortedOffers.sort((a, b) => a.totalPayments - b.totalPayments);
  }

  // Lower monthly payment: sort by term high to low
  if (goals.includes(LoanGoal.LowerMonthlyPayment)) {
    return sortedOffers.sort((a, b) => b.totalPayments - a.totalPayments);
  }

  // If none of the above conditions are met, default to term low to high
  return sortedOffers.sort((a, b) => a.totalPayments - b.totalPayments);
};

export const getOfferBadgeType = (offer: OfferByAmount, allOffers: OfferByAmount[]): OfferCardBadgeType | undefined => {
  if (allOffers.length <= 1) return undefined;

  const minTerm = Math.min(...allOffers.map((o) => o.totalPayments));
  const maxTerm = Math.max(...allOffers.map((o) => o.totalPayments));

  if (offer.totalPayments === minTerm) return OfferCardBadgeType.FasterPayoff;
  if (offer.totalPayments === maxTerm) return OfferCardBadgeType.LowerPayment;

  return undefined;
};

export const getHeaderInfo = (
  additionalFundsOnly: boolean,
  eligibleForSingleAmountLoan: boolean,
  maxLoanAmount: number,
  maxPossibleConsolidationAmount: number,
): HeaderInfo => {
  if (additionalFundsOnly) {
    return { title: `You're qualified for up to $${formatNumber(maxLoanAmount)}!`, showSubtitle: false };
  }

  if (eligibleForSingleAmountLoan) {
    return { title: `Pay off $${formatNumber(maxPossibleConsolidationAmount)} of debt`, showSubtitle: true };
  }

  return { title: 'How much debt do you want to pay off?', showSubtitle: true };
};
